import {
  takeLatest,
  put,
  select,
  call,
  all,
} from 'redux-saga/effects';
import { message } from 'antd';
import { isEqual } from 'lodash';
import * as compareDrawerTypes from '@store/compareDrawer/types';
import {
  selectedCustomerIdSelector,
  selectedUserNameSelector,
} from '@store/common/selectors';
import { parseDashboardURL } from '@utils';
import scrollingWindowFromTo, { cloneWithoutFromTo } from '@utils/scrollingWindowFromTo';

import { CLOSE_ALL_TABS } from '@store/common/types';
import * as actions from './actions';
import * as types from './types';
import { getNewTab, TabsManager } from './utils';

import tabsStoreSelector, { tabsSelector } from './selectors';
import { globalRouter } from '@src/utils/globalRouter';

const tmInstance = {};
function* getTabsManager() {
  const customerId = yield select(selectedCustomerIdSelector);
  const username = yield select(selectedUserNameSelector);

  const tabsMapId = `${customerId}-${username}`;

  if (tmInstance.tabsMapId !== tabsMapId) {
    return new TabsManager(tabsMapId);
  }
  return tmInstance;
}

function* changeTabs({ payload, id, isRemove }) {

  const puts = [];
  if (isRemove) {
    puts.push(put(actions.triggerRemoveTab(id)));
  } else if (id) {
    puts.push(put(actions.triggerUpdateTab(payload, id)));
  } else {
    puts.push(put(actions.triggerAddTab(payload)));
  }

  puts.push(put({
    type: compareDrawerTypes.EXIT_COMPARE_MODE,
  }));
  yield all(puts);
}

function* addTab({ payload }) {
  const tabsList = yield select(tabsSelector);

  const tabsStore = yield select(tabsStoreSelector);

  const tempTab = getNewTab(tabsList);

  const parsedPayload = scrollingWindowFromTo(payload);
  const tab = { ...tempTab, ...parsedPayload };

  const tabsManager = yield call(getTabsManager);
  const tabs = tabsManager.parseTabs(tabsStore, tab, tab.id);

  const calls = [];
  calls.push(put({ type: types.CHANGE_TABS, tabs }));

  const { isSharing } = payload;
  yield all(calls);

  if (!isSharing) {
    globalRouter.navigate(parseDashboardURL(tab));
  }
}

function* updateTab({
  payload,
  id,
  removeHasChanged,
}) {
  const tabsList = yield select(tabsSelector);
  const tabsStore = yield select(tabsStoreSelector);

  const tempTab = tabsList.find(item => `${item.id}` === `${id}`) || {};

  /* jsonParsed is used for deep omitting undefined properties to compare objects  */
  const jsonParsed = obj => JSON.parse(JSON.stringify(obj));

  if (removeHasChanged) {
    delete tempTab.hasChanged;
  } else if (tempTab && payload) {
    const tempTabConfig = cloneWithoutFromTo(tempTab.config);
    const payloadConfig = cloneWithoutFromTo(payload.config);
    // eslint-disable-next-line no-empty
    if (tempTabConfig == null || payloadConfig == null) {
    } else if (!isEqual(jsonParsed(tempTabConfig), jsonParsed(payloadConfig))) {
      tempTab.hasChanged = true;
    }
  }

  const tab = { ...tempTab, ...payload };

  const tabsManager = yield call(getTabsManager);
  const tabs = tabsManager.parseTabs(tabsStore, tab, tab.id);

  yield put({ type: types.CHANGE_TABS, tabs });
  // don't change route after changing metric for a sharing page
  const { isSharing } = payload;
  if (!isSharing) {
    globalRouter.navigate(parseDashboardURL(tab));
  }
}

function* removeTab({ id, msg }) {
  const tabsStore = yield select(tabsStoreSelector);

  const tabsManager = yield call(getTabsManager);
  const tabs = tabsManager.parseTabs(tabsStore, {}, id, true);

  try {
    yield all([
      put({ type: types.CHANGE_TABS, tabs }),
      call(message.success, msg || 'Removed successfully', 1.5),
    ]);
    globalRouter.navigate(parseDashboardURL({}));
  } catch (error) {
    yield call(message.error, 'Error while removing');
  }
}

function*  removeBackgroundTab({ id }) {
  const tabsStore = yield select(tabsStoreSelector);
  const tabsManager = yield call(getTabsManager);
  const tabs = tabsManager.parseTabs(tabsStore, {}, id, true);

  try {
    yield put({ type: types.CHANGE_TABS, tabs });
  } catch (error) {
    yield call(message.error, 'Error while removing tab');
  }
}

function* replaceTab({ fromId, toId, payload }) {
  const tabsStore = yield select(tabsStoreSelector);

  const tabsManager = yield call(getTabsManager);
  const tabs = tabsManager.replaceTab(tabsStore, payload, fromId, toId);

  yield all([
    put({ type: types.CHANGE_TABS, tabs }),
  ]);
}

function* initTabs() {
  const tabsStore = yield select(tabsStoreSelector);

  const tabsManager = yield call(getTabsManager);

  const tabs = tabsManager.parseTabs(tabsStore);
  yield put({ type: types.CHANGE_TABS, tabs });
}

function* closeAllTempTabs() {
  const tabsManager = yield call(getTabsManager);
  tabsManager.removeTabs();
  yield initTabs();
}


function* watchInitTabs() {
  yield takeLatest([
    types.INIT,
  ], initTabs);
}

function* watchTriggerChangeTabs() {
  yield takeLatest([
    types.TRIGGER_CHANGE_TABS,
  ], changeTabs);
}

function* watchTriggerAddTab() {
  yield takeLatest([
    types.TRIGGER_ADD_TAB,
  ], addTab);
}
function* watchTriggerUpdateTab() {
  yield takeLatest([
    types.TRIGGER_UPDATE_TAB,
  ], updateTab);
}
function* watchTriggerRemoveTab() {
  yield takeLatest([
    types.TRIGGER_REMOVE_TAB,
  ], removeTab);
}
function* watchTriggerReplaceTab() {
  yield takeLatest([
    types.TRIGGER_REPLACE_TAB,
  ], replaceTab);
}

function* watchTriggerRemoveBackgroundTab() {
  yield takeLatest([
    types.REMOVE_BACKGROUND_TAB,
  ], removeBackgroundTab);
}

function* watchCloseAllTabs() {
  yield takeLatest(CLOSE_ALL_TABS, closeAllTempTabs);
}

export default [
  watchInitTabs,
  watchTriggerChangeTabs,
  watchTriggerAddTab,
  watchTriggerUpdateTab,
  watchTriggerRemoveTab,
  watchTriggerReplaceTab,
  watchCloseAllTabs,
  watchTriggerRemoveBackgroundTab,
];
